import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Play, Workflow, BarChart3, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { KineticHeadline, Highlight } from "@/components/motion";

const TABS = [
  { id: "automation", label: "Automation", icon: Workflow },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "crm", label: "CRM", icon: Users },
];

const AutomationPane = () => (
  <div className="space-y-2.5 font-mono-brand text-[11px]">
    {[
      ["09:41", "Invoice batch ingested", "done"],
      ["09:42", "148 records extracted", "done"],
      ["09:44", "Syncing to AgencyOrbits", "running"],
    ].map(([t, msg, s]) => (
      <div key={t} className="flex items-center gap-2.5 bg-[#F8FAFC] rounded-xl px-3.5 py-2.5">
        <span className="text-slate-400">{t}</span>
        <span className="flex-1 text-[#01193D] font-medium truncate">{msg}</span>
        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${s === "done" ? "bg-emerald-50 text-emerald-600" : "bg-[#CAF2F9] text-[#2563EB]"}`}>{s}</span>
      </div>
    ))}
    <div className="flex items-center gap-2.5 bg-[#F8FAFC] rounded-xl px-3.5 py-2.5">
      <span className="text-slate-400">09:45</span>
      <span className="text-[#01193D] font-medium">Agent composing summary</span>
      <span className="flex gap-1 ml-1">
        <span className="typing-dot w-1.5 h-1.5 rounded-full bg-[#2563EB]" />
        <span className="typing-dot w-1.5 h-1.5 rounded-full bg-[#2563EB]" />
        <span className="typing-dot w-1.5 h-1.5 rounded-full bg-[#2563EB]" />
      </span>
    </div>
  </div>
);

const AnalyticsPane = () => (
  <div>
    <div className="flex items-end gap-2 h-28 mb-4">
      {[38, 62, 45, 78, 56, 92, 70, 100].map((h, i) => (
        <motion.div
          key={i}
          initial={{ height: 0 }}
          animate={{ height: `${h}%` }}
          transition={{ delay: i * 0.07, duration: 0.5, ease: "easeOut" }}
          className="flex-1 rounded-t-lg bg-gradient-to-t from-[#2563EB] to-[#09B2ED]"
          style={{ opacity: 0.35 + (h / 100) * 0.65 }}
        />
      ))}
    </div>
    <div className="grid grid-cols-3 gap-2.5">
      {[["MRR", "$84k"], ["Conv.", "6.2%"], ["Churn", "0.8%"]].map(([l, v]) => (
        <div key={l} className="bg-[#F8FAFC] rounded-xl px-3 py-2 text-center">
          <p className="font-display font-bold text-sm text-[#01193D]">{v}</p>
          <p className="text-[10px] text-slate-400">{l}</p>
        </div>
      ))}
    </div>
  </div>
);

const CrmPane = () => (
  <div className="grid grid-cols-3 gap-2.5">
    {[["Leads", 2, "#91E0F1"], ["Active", 2, "#09B2ED"], ["Won", 1, "#2563EB"]].map(([col, n, c]) => (
      <div key={col} className="bg-[#F8FAFC] rounded-xl p-2.5">
        <div className="flex items-center justify-between mb-2 px-0.5">
          <span className="text-[10px] font-semibold text-[#01193D]">{col}</span>
          <span className="w-4 h-4 rounded-full text-[8px] font-bold text-white flex items-center justify-center" style={{ background: c }}>{n}</span>
        </div>
        {[...Array(n)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg border border-slate-100 p-2 mb-1.5">
            <div className="h-1.5 rounded-full bg-slate-100 w-4/5 mb-1.5" />
            <div className="flex justify-between items-center">
              <span className="w-4 h-4 rounded-full bg-gradient-to-br from-[#2563EB] to-[#09B2ED]" />
              <span className="h-1.5 rounded-full w-6" style={{ background: `${c}66` }} />
            </div>
          </div>
        ))}
      </div>
    ))}
  </div>
);

const PANES = { automation: <AutomationPane />, analytics: <AnalyticsPane />, crm: <CrmPane /> };

const CHIPS = [
  { key: "efficiency", node: <p className="text-xs"><span className="font-display font-bold text-[#01193D]">+34%</span> <span className="text-slate-500">Efficiency</span></p> },
  {
    key: "avatars",
    node: (
      <div className="flex items-center gap-2">
        <div className="flex -space-x-2">
          {["#2563EB", "#09B2ED", "#91E0F1"].map((c) => (
            <span key={c} className="w-6 h-6 rounded-full border-2 border-white" style={{ background: c }} />
          ))}
        </div>
        <span className="text-[11px] text-slate-500 font-medium">50+ clients</span>
      </div>
    ),
  },
  { key: "deployed", node: <p className="text-xs font-semibold text-[#01193D]">Deployed in <span className="text-[#2563EB]">14 days</span></p> },
];

const DemoPanel = () => {
  const [tab, setTab] = useState("automation");
  return (
    <div className="relative max-w-md mx-auto" data-testid="hero-demo-panel">
      <div className="animate-float-slow backdrop-blur-xl bg-white/75 border border-white/90 rounded-[20px] shadow-[0_20px_60px_rgba(1,25,61,0.12)] p-5">
        <div className="flex items-center gap-1.5 mb-4">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`hero-demo-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                tab === t.id ? "btn-gradient text-white" : "bg-white/70 text-[#475569] hover:text-[#2563EB]"
              }`}
            >
              <t.icon size={12} /> {t.label}
            </button>
          ))}
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4 min-h-[210px]">
          <AnimatePresence mode="wait">
            <motion.div key={tab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }} data-testid={`hero-demo-pane-${tab}`}>
              {PANES[tab]}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
      {/* floating chips — desktop */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.0, duration: 0.5 }}
        className="animate-float hidden sm:block absolute bg-white rounded-2xl soft-shadow border border-slate-100 px-4 py-3"
        style={{ left: "-96px", top: "40px" }}
      >
        {CHIPS[0].node}
      </motion.div>
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.2, duration: 0.5 }}
        className="animate-float-slow hidden sm:block absolute bg-white rounded-2xl soft-shadow border border-slate-100 px-4 py-3"
        style={{ right: "-112px", top: "25%", animationDelay: "2.2s" }}
      >
        {CHIPS[1].node}
      </motion.div>
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.4, duration: 0.5 }}
        className="animate-float hidden sm:block absolute bg-white rounded-2xl soft-shadow border border-slate-100 px-4 py-3"
        style={{ left: "-64px", bottom: "-24px", animationDelay: "3.6s" }}
      >
        {CHIPS[2].node}
      </motion.div>
      <div className="sm:hidden flex gap-3 mt-4 overflow-x-auto snap-x pb-1" data-testid="hero-chips-mobile">
        {CHIPS.map((c) => (
          <div key={c.key} className="snap-start shrink-0 bg-white rounded-2xl soft-shadow border border-slate-100 px-4 py-3">{c.node}</div>
        ))}
      </div>
    </div>
  );
};

export default function HeroSection() {
  const [reelOpen, setReelOpen] = useState(false);
  const gradientStyles = `
    @keyframes float-left {
      0% { transform: translate(0px, 0px) scale(1); }
      50% { transform: translate(50px, -30px) scale(1.12); }
      100% { transform: translate(0px, 0px) scale(1); }
    }
    @keyframes float-right {
      0% { transform: translate(0px, 0px) scale(1); }
      50% { transform: translate(-40px, 20px) scale(0.92); }
      100% { transform: translate(0px, 0px) scale(1); }
    }
    @keyframes float-center {
      0% { transform: translate(0px, 0px) scale(1); }
      50% { transform: translate(-20px, -20px) scale(1.08); }
      100% { transform: translate(0px, 0px) scale(1); }
    }
  `;
  return (
    <section className="relative min-h-screen flex flex-col justify-between overflow-hidden pt-28 pb-0 bg-white" data-testid="hero-section">
      <style>{gradientStyles}</style>
      
      {/* Background base */}
      <div className="absolute inset-0 bg-white pointer-events-none" />

      {/* Subtle Studio Ambient Lighting */}
      <div 
        className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full blur-[100px] opacity-25 pointer-events-none" 
        style={{ 
          background: "radial-gradient(circle, #CAF2F9 0%, #91E0F1 50%, transparent 100%)" 
        }} 
      />
      <div 
        className="absolute -top-32 -right-32 w-[550px] h-[550px] rounded-full blur-[120px] opacity-15 pointer-events-none" 
        style={{ 
          background: "radial-gradient(circle, #2563EB 0%, #09B2ED 50%, transparent 100%)" 
        }} 
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 flex flex-col items-center justify-center text-center w-full z-20 flex-1">
        <div className="max-w-6xl flex flex-col items-center mt-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="font-mono-brand inline-flex items-center gap-2 bg-[#CAF2F9]/50 border border-[#91E0F1]/40 text-[#2563EB] text-[11px] font-semibold tracking-[0.14em] uppercase px-3.5 py-1.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-[#09B2ED]" /> AI-Powered Digital Transformation
            </span>
          </motion.div>
          <h1 className="font-display font-bold text-[#01193D] text-3xl sm:text-[38px] lg:text-[44px] leading-[1.15] tracking-tight mt-5 max-w-6xl">
            <div className="block">
              <KineticHeadline before="Empowering businesses through" />
            </div>
            <div className="block mt-1 sm:mt-2">
              <KineticHeadline gradient="intelligent technology" />
            </div>
          </h1>
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.35 }} className="text-[#475569] text-base lg:text-lg leading-relaxed mt-4 max-w-2xl mx-auto">
            AI, modern engineering, and design — combined to solve <Highlight delay={0.65}>real business problems</Highlight> and create <Highlight type="underline" delay={0.85}>measurable growth</Highlight>.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.48 }} className="flex flex-wrap justify-center gap-4 mt-6">
            <Link to="/contact" data-testid="hero-primary-cta" className="btn-gradient group text-white text-sm font-semibold px-6 py-3 rounded-full inline-flex items-center gap-2">
              Start Your Project <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <button
              onClick={() => setReelOpen(true)}
              data-testid="hero-watch-reel-button"
              className="group inline-flex items-center gap-2.5 text-sm font-semibold text-[#01193D] px-5 py-2.5 rounded-full hover:bg-[#CAF2F9]/50 transition-colors"
            >
              <span className="w-8 h-8 rounded-full border-1.5 border border-[#01193D]/20 flex items-center justify-center group-hover:border-[#2563EB] group-hover:text-[#2563EB] transition-colors">
                <Play size={12} className="ml-0.5" />
              </span>
              Watch how we work
            </button>
          </motion.div>
        </div>
      </div>

      {/* Skyline Image resting at bottom - Full Width */}
      <motion.div 
        initial={{ opacity: 0, y: 30 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ duration: 0.8, delay: 0.55 }}
        className="w-full max-w-5xl mx-auto relative z-0 -mt-[40px] md:-mt-[100px]"
      >
        <img 
          src="/tech_skyline.png" 
          alt="Tech Skyline" 
          className="w-full h-auto select-none pointer-events-none block contrast-[1.04] brightness-[1.01] opacity-35"
          style={{ 
            imageRendering: "auto",
            WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 112px, black calc(100% - 112px), transparent 100%)",
            maskImage: "linear-gradient(to right, transparent 0%, black 112px, black calc(100% - 112px), transparent 100%)"
          }}
        />
      </motion.div>

      <Dialog open={reelOpen} onOpenChange={setReelOpen}>
        <DialogContent className="rounded-3xl max-w-lg" data-testid="reel-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-[#01193D]">How we work — 90-second reel</DialogTitle>
          </DialogHeader>
          <div className="aspect-video rounded-2xl bg-[#01193D] flex flex-col items-center justify-center gap-3">
            <span className="w-14 h-14 rounded-full btn-gradient flex items-center justify-center">
              <Play size={20} className="text-white ml-0.5" />
            </span>
            <p className="text-slate-300 text-sm">Reel coming soon — we're filming it now.</p>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
